import React from 'react';
import { T } from './login.theme';
import { COPY } from './login.copy';
import { PlusIcon, InfoIcon, PeopleIcon } from './LoginIcons';
import WaiterTile from './WaiterTile';

/**
 * The waiter panel, anchored to the top-right corner of the login screen.
 *
 * Two doors are open at once: waiters pick a face here, everyone else types on
 * the left. Neither hides the other, so there is no mode to find.
 *
 * The rail keeps its width when empty — otherwise the sign-in card would slide
 * sideways the moment the first tile lands.
 */
export default function WaiterRail({ profiles, onOpen, onAdd, onRemove }) {
  const [managing, setManaging] = React.useState(false);
  const hasProfiles = profiles.length > 0;

  // Leaving Manage mode has to happen when the last tile goes, or the header
  // is left offering Done over an empty grid.
  React.useEffect(() => {
    if (!hasProfiles) setManaging(false);
  }, [hasProfiles]);

  return (
    <aside
      style={{
        width: T.railWidth,
        flexShrink: 0,
        height: '100%',
        background: T.surface,
        borderLeft: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '24px 24px 18px',
          borderBottom: `1px solid ${T.hairline}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: -0.1, color: T.ink }}>
            {COPY.waiters}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {hasProfiles ? (
              <button
                type="button"
                onClick={() => setManaging((on) => !on)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: T.controlHeight,
                  padding: managing ? '0 12px' : '0 10px',
                  background: managing ? T.ink : 'transparent',
                  border: 'none',
                  borderRadius: T.radiusControl,
                  font: 'inherit',
                  fontSize: 12.5,
                  fontWeight: managing ? 700 : 600,
                  color: managing ? T.surface : T.inkSoft,
                  cursor: 'pointer',
                }}
              >
                {managing ? COPY.done : COPY.manage}
              </button>
            ) : null}

            {managing ? null : (
              <button
                type="button"
                onClick={onAdd}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  height: T.controlHeight,
                  padding: '0 13px',
                  background: T.selectedBg,
                  border: `1px solid ${T.selectedBorder}`,
                  borderRadius: T.radiusControl,
                  font: 'inherit',
                  color: T.brandInk,
                  cursor: 'pointer',
                }}
              >
                <PlusIcon />
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>{COPY.addWaiter}</span>
              </button>
            )}
          </div>
        </div>

        <span style={{ fontSize: 12.5, color: T.inkSoft, lineHeight: 1.45 }}>
          {COPY.waitersSub}
        </span>
      </div>

      {hasProfiles ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 12,
            padding: '20px 24px',
            overflowY: 'auto',
          }}
        >
          {profiles.map((profile) => (
            <WaiterTile
              key={profile.id}
              profile={profile}
              managing={managing}
              onOpen={onOpen}
              onRemove={onRemove}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: 32,
            textAlign: 'center',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 52,
              height: 52,
              border: `1.5px dashed ${T.borderStrong}`,
              borderRadius: T.radiusPill,
              color: T.inkGhost,
            }}
          >
            <PeopleIcon size={22} />
          </span>
          <span style={{ fontSize: 12.5, color: T.inkSoft, lineHeight: 1.55 }}>
            {COPY.noWaitersTitle}
            <br />
            {COPY.noWaitersBody}
          </span>
        </div>
      )}

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          padding: '18px 24px 22px',
          borderTop: `1px solid ${T.hairline}`,
          color: T.inkFaint,
        }}
      >
        <span style={{ display: 'flex', paddingTop: 1 }}>
          <InfoIcon />
        </span>
        <span style={{ fontSize: 11.5, lineHeight: 1.5 }}>{COPY.storageNote}</span>
      </div>
    </aside>
  );
}
